import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          console.error('[AUTH CALLBACK] OAuth error:', error, errorDescription);
          toast.error(errorDescription || 'Authentication failed');
          navigate('/auth', { replace: true });
          return;
        }

        // Check for code in URL (PKCE flow)
        const code = searchParams.get('code');
        
        if (code) {
          console.log('[AUTH CALLBACK] Processing OAuth code...');
          
          // Exchange the code for session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
            console.error('[AUTH CALLBACK] Error exchanging code:', exchangeError);
            toast.error(exchangeError.message || 'Authentication failed');
            navigate('/auth', { replace: true });
            return;
          }

          if (data.session) {
            console.log('[AUTH CALLBACK] Session created successfully');
            toast.success('Welcome! You are now signed in.');
            navigate('/', { replace: true });
            return;
          }
        }

        // If no code, check if we already have a session (implicit flow or already authenticated)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[AUTH CALLBACK] Session error:', sessionError);
          toast.error('Authentication failed');
          navigate('/auth', { replace: true });
          return;
        }

        if (session) {
          console.log('[AUTH CALLBACK] Existing session found');
          toast.success('Welcome back!');
          navigate('/', { replace: true });
        } else {
          console.log('[AUTH CALLBACK] No session found, redirecting to auth');
          navigate('/auth', { replace: true });
        }
      } catch (error) {
        console.error('[AUTH CALLBACK] Unexpected error:', error);
        toast.error('Authentication failed');
        navigate('/auth', { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  if (!isProcessing) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}
